"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Search, X, ArrowUpRight, Loader2 } from "lucide-react";
import { useGlobalSearch } from "@/hooks/use-global-search";
import { searchCategories } from "@/types/search";

export default function SiteSearch({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>([]);
  const [selected, setSelected] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { results, loading, error, cooldown, retry } = useGlobalSearch(
    query,
    open,
  );
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("recentSearches") || "[]");
      if (Array.isArray(saved))
        setRecent(
          saved
            .filter(
              (v) => typeof v === "string" && v.length >= 2 && v.length <= 80,
            )
            .slice(0, 3),
        );
    } catch {
      /* Storage can be unavailable in private browsing. */
    }
  }, []);
  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);
  useEffect(() => setSelected(-1), [query, results]);
  const remember = () => {
    const term = query.trim();
    if (term.length < 2) return;
    const next = [term, ...recent.filter((v) => v !== term)].slice(0, 3);
    setRecent(next);
    try {
      localStorage.setItem("recentSearches", JSON.stringify(next));
    } catch {
      /* Optional local history. */
    }
  };
  const finish = () => {
    remember();
    setQuery("");
    onClose();
  };
  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    if (query.trim().length < 2) return;
    router.push(`/search?${new URLSearchParams({ q: query.trim() })}`);
    finish();
  };
  if (!open) return null;
  return (
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="site-search-label"
      className="relative z-[60] w-full border-y border-line bg-surface text-ink"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose();
        }
        if (event.key === "Tab") {
          const items = [
            ...(panel.current?.querySelectorAll<HTMLElement>(
              "input, button:not(:disabled), a[href]",
            ) || []),
          ];
          const first = items[0],
            last = items.at(-1);
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      }}
    >
      <div className="mx-auto max-w-[70rem] px-4 sm:px-6 py-5 max-h-[calc(100dvh-145px)] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 id="site-search-label" className="text-sm font-semibold">
            Search Techno Music World
          </h2>
          <button
            type="button"
            aria-label="Close search"
            onClick={onClose}
            className="p-2 text-muted hover:text-ink focus-visible:outline focus-visible:outline-accent"
          >
            <X size={20} />
          </button>
        </div>
        <form
          action="/search"
          method="get"
          onSubmit={submit}
          className="flex gap-2"
        >
          <label className="relative flex-1 min-w-0">
            <span className="sr-only">
              Search artists, events, music and stories
            </span>
            <Search
              size={18}
              aria-hidden="true"
              className="absolute left-3 top-3.5 text-subtle"
            />
            <input
              ref={input}
              id="global-search-input"
              name="q"
              type="search"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={results.length > 0}
              aria-controls="global-search-results"
              aria-activedescendant={
                selected >= 0 ? `global-search-option-${selected}` : undefined
              }
              autoComplete="off"
              minLength={2}
              maxLength={80}
              required
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Artists, events, music, stories..."
              className="w-full rounded border border-line-strong bg-canvas py-3 pl-10 pr-3 text-sm outline-none focus:border-accent"
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" && results.length) {
                  e.preventDefault();
                  setSelected((v) => (v + 1) % results.length);
                }
                if (e.key === "ArrowUp" && results.length) {
                  e.preventDefault();
                  setSelected((v) =>
                    v < 0
                      ? results.length - 1
                      : (v - 1 + results.length) % results.length,
                  );
                }
                if (e.key === "Enter" && selected >= 0 && results[selected]) {
                  e.preventDefault();
                  const url = results[selected].url;
                  if (url.startsWith("https://")) window.location.assign(url);
                  else router.push(url);
                  finish();
                }
              }}
            />
          </label>
          <button
            type="submit"
            className="rounded bg-theme-neon text-on-accent px-4 text-sm font-bold disabled:opacity-50"
            disabled={query.trim().length < 2}
          >
            Search
          </button>
        </form>
        <div className="mt-4" aria-live="polite">
          {loading ? (
            <p className="flex items-center gap-2 py-5 text-sm text-muted">
              <Loader2 size={16} className="animate-spin" />
              Searching...
            </p>
          ) : error ? (
            <div className="py-4">
              <p className="text-sm text-ink-secondary">
                {cooldown
                  ? `Please wait ${cooldown} seconds before searching again.`
                  : error}
              </p>
              <button
                type="button"
                disabled={cooldown > 0}
                onClick={retry}
                className="mt-3 text-sm text-accent disabled:opacity-40"
              >
                Try again
              </button>
            </div>
          ) : results.length ? (
            <>
              <ul
                id="global-search-results"
                role="listbox"
                aria-label="Suggested results"
                className="divide-y divide-line"
              >
                {results.map((result, index) => (
                  <li role="presentation" key={`${result.type}-${result.id}`}>
                    <Link
                      prefetch={false}
                      role="option"
                      aria-selected={selected === index}
                      id={`global-search-option-${index}`}
                      href={result.url}
                      onClick={finish}
                      className={`flex items-center justify-between gap-3 px-3 py-3 focus-visible:outline focus-visible:outline-accent ${selected === index ? "bg-surface-raised" : "hover:bg-surface"}`}
                    >
                      <span className="min-w-0">
                        <span className="block text-[11px] text-subtle uppercase tracking-wide">
                          {
                            searchCategories.find(
                              ([type]) => type === result.type,
                            )?.[1]
                          }
                        </span>
                        <span className="block text-sm text-ink truncate">
                          {result.title}
                        </span>
                      </span>
                      <ArrowUpRight
                        size={16}
                        className="shrink-0 text-subtle"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                prefetch={false}
                href={`/search?${new URLSearchParams({ q: query.trim() })}`}
                onClick={finish}
                className="inline-flex py-4 text-sm text-accent"
              >
                View all results
              </Link>
            </>
          ) : query.trim().length >= 2 ? (
            <p className="py-5 text-sm text-muted">
              No matches yet. Try an artist, city or event name.
            </p>
          ) : (
            <div className="py-3">
              <p className="text-xs text-subtle mb-3">
                {recent.length ? "Recent searches" : "Try a search"}
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-3">
                {(recent.length ? recent : ["OGUZ", "Berlin", "Teletech"]).map(
                  (term) => (
                    <Link
                      prefetch={false}
                      key={term}
                      href={`/search?${new URLSearchParams({ q: term })}`}
                      onClick={onClose}
                      className="text-sm text-ink-secondary hover:text-accent"
                    >
                      {term}
                    </Link>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
